import { isDefined } from "@clipboard-health/util-ts";
import BusinessIcon from "@mui/icons-material/Business";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import PersonIcon from "@mui/icons-material/Person";
import { Box, List, ListItemButton, ListItemIcon, ListItemText } from "@mui/material";
import CircularProgress from "@mui/material/CircularProgress";
import { Text } from "@src/appV2/redesign/components/Text";
import { type Agent } from "@src/appV2/User/api/useSearchAgents";
import { type Facility } from "@src/appV2/User/api/useSearchFacilities";
import { useEffect, useRef } from "react";

import { type WorkerShift } from "./api/useGetWorkerShifts";
import { type ShiftSearchResult } from "./api/useSearchShift";
import { SectionHeader } from "./components/SectionHeader";
import { type ShiftAction, ShiftActionItem } from "./components/ShiftActionItem";
import { ShiftResultItem } from "./components/ShiftResultItem";
import { WorkerShiftItem } from "./components/WorkerShiftItem";
import { type SearchResultItem } from "./hooks/useOmnisearchResults";

interface OmnisearchResultsListProps {
  agents: Agent[];
  facilities: Facility[];
  shift: ShiftSearchResult | null;
  expandedShiftId: string | null;
  expandedWorkerId: string | null;
  workerShifts: WorkerShift[];
  isLoadingWorkerShifts: boolean;
  workerHasShifts: boolean;
  selectedIndex: number;
  copiedItem: ShiftAction | "shift" | null;
  onSelect: (item: SearchResultItem) => void;
}

export function OmnisearchResultsList(props: OmnisearchResultsListProps) {
  const {
    agents,
    facilities,
    shift,
    expandedShiftId,
    expandedWorkerId,
    workerShifts,
    isLoadingWorkerShifts,
    workerHasShifts,
    selectedIndex,
    copiedItem,
    onSelect,
  } = props;
  const isShiftExpanded = isDefined(shift) && expandedShiftId === shift.id;
  const itemRefs = useRef<Map<number, HTMLElement>>(new Map());

  useEffect(() => {
    const element = itemRefs.current.get(selectedIndex);
    element?.scrollIntoView?.({ block: "nearest", behavior: "smooth" });
  }, [selectedIndex]);

  const registerRef = (index: number) => (element: HTMLElement | null) => {
    if (element) {
      itemRefs.current.set(index, element);
    } else {
      itemRefs.current.delete(index);
    }
  };

  const getAgentResultIndex = (agentIndex: number): number => {
    let index = 0;
    for (let agentNumber = 0; agentNumber < agentIndex; agentNumber++) {
      index += 1;
      if (expandedWorkerId === agents[agentNumber].userId) {
        index += workerShifts.length;
      }
    }
    return index;
  };

  const getFacilitiesBaseIndex = (): number => {
    let index = agents.length;
    if (isDefined(expandedWorkerId)) {
      index += workerShifts.length;
    }
    return index;
  };

  const getShiftBaseIndex = (): number => getFacilitiesBaseIndex() + facilities.length;
  const facilitiesBaseIndex = getFacilitiesBaseIndex();
  const shiftBaseIndex = getShiftBaseIndex();

  return (
    <List sx={{ py: 0 }}>
      {agents.length > 0 && (
        <>
          <SectionHeader label="Professionals" />
          {agents.map((agent, agentIndex) => {
            const isExpanded = expandedWorkerId === agent.userId;
            const resultIndex = getAgentResultIndex(agentIndex);
            const agentSelected = selectedIndex === resultIndex;
            return (
              <Box key={agent.userId}>
                <ListItemButton
                  ref={registerRef(resultIndex)}
                  selected={agentSelected}
                  sx={{
                    py: 1,
                    px: 2,
                    "&.Mui-selected": {
                      backgroundColor: "action.selected",
                    },
                  }}
                  onClick={() => {
                    onSelect({ type: "agent", data: agent, expanded: isExpanded });
                  }}
                >
                  <ListItemIcon sx={{ minWidth: 40 }}>
                    <PersonIcon color="primary" />
                  </ListItemIcon>
                  <ListItemText
                    primary={
                      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                        <Text sx={{ fontWeight: 500 }}>{agent.name}</Text>
                        {agentSelected &&
                          (isExpanded ? (
                            <ExpandMoreIcon fontSize="small" />
                          ) : (
                            <ChevronRightIcon fontSize="small" />
                          ))}
                        {isLoadingWorkerShifts && isExpanded && <CircularProgress size={14} />}
                      </Box>
                    }
                    secondary={agent.email}
                    secondaryTypographyProps={{ variant: "caption" }}
                  />
                </ListItemButton>
                {isExpanded && !isLoadingWorkerShifts && workerHasShifts && (
                  <>
                    {workerShifts.map((workerShift, shiftIndex) => {
                      const shiftResultIndex = resultIndex + 1 + shiftIndex;
                      return (
                        <WorkerShiftItem
                          key={workerShift.id}
                          ref={registerRef(shiftResultIndex)}
                          shift={workerShift}
                          selected={selectedIndex === shiftResultIndex}
                          copied={copiedItem === "shift" && selectedIndex === shiftResultIndex}
                          onClick={() => {
                            onSelect({
                              type: "agent-shift",
                              data: workerShift,
                              workerId: agent.userId,
                            });
                          }}
                        />
                      );
                    })}
                  </>
                )}
                {isExpanded && !isLoadingWorkerShifts && !workerHasShifts && (
                  <Box sx={{ pl: 6, pr: 2, py: 1 }}>
                    <Text variant="caption" color="text.secondary">
                      No recent or upcoming shifts
                    </Text>
                  </Box>
                )}
              </Box>
            );
          })}
        </>
      )}
      {facilities.length > 0 && (
        <>
          <SectionHeader label="Workplaces" hasTopMargin={agents.length > 0} />
          {facilities.map((facility, index) => {
            const resultIndex = facilitiesBaseIndex + index;
            return (
              <ListItemButton
                key={facility.userId}
                ref={registerRef(resultIndex)}
                selected={selectedIndex === resultIndex}
                sx={{
                  py: 1,
                  px: 2,
                  "&.Mui-selected": {
                    backgroundColor: "action.selected",
                  },
                }}
                onClick={() => {
                  onSelect({ type: "facility", data: facility });
                }}
              >
                <ListItemIcon sx={{ minWidth: 40 }}>
                  <BusinessIcon color="secondary" />
                </ListItemIcon>
                <ListItemText
                  primary={facility.name}
                  primaryTypographyProps={{ fontWeight: 500 }}
                  secondary={`${facility.type} • ${facility.status}`}
                  secondaryTypographyProps={{ variant: "caption" }}
                />
              </ListItemButton>
            );
          })}
        </>
      )}
      {isDefined(shift) && (
        <>
          <SectionHeader label="Shifts" hasTopMargin={agents.length > 0 || facilities.length > 0} />
          <ShiftResultItem
            ref={registerRef(shiftBaseIndex)}
            shift={shift}
            selected={selectedIndex === shiftBaseIndex}
            expanded={isShiftExpanded}
            onClick={() => {
              onSelect({ type: "shift", data: shift, expanded: isShiftExpanded });
            }}
          />
          {isShiftExpanded && (
            <>
              {isDefined(shift.agent?.userId) && (
                <ShiftActionItem
                  ref={registerRef(shiftBaseIndex + 1)}
                  shift={shift}
                  action="worker"
                  selected={selectedIndex === shiftBaseIndex + 1}
                  copied={copiedItem === "worker"}
                  onClick={() => {
                    onSelect({ type: "shift-action", data: shift, action: "worker" });
                  }}
                />
              )}
              {isDefined(shift.facility?.userId) && (
                <ShiftActionItem
                  ref={registerRef(shiftBaseIndex + 1 + (isDefined(shift.agent?.userId) ? 1 : 0))}
                  shift={shift}
                  action="workplace"
                  selected={
                    selectedIndex === shiftBaseIndex + 1 + (isDefined(shift.agent?.userId) ? 1 : 0)
                  }
                  copied={copiedItem === "workplace"}
                  onClick={() => {
                    onSelect({ type: "shift-action", data: shift, action: "workplace" });
                  }}
                />
              )}
            </>
          )}
        </>
      )}
    </List>
  );
}
